"use client"

import { useState } from "react"
import { ArrowLeft, Loader2, Plus, Scale } from "lucide-react"
import { useAdminPartnerDetailQuery } from "@/hooks/queries/partners"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { NameWithBadges } from "@/components/partners/name-with-badges"
import { formatMicroUsd } from "@/lib/billing/utils/format"
import { PARTNER_ACCOUNT_STATUSES } from "@/lib/partners/constants"
import { formatPartnerDate } from "@/components/partners/format-date"
import { bonusScheduleLabel, type PartnerBonusTranslator } from "@/components/partners/client-bonus"
import { formatPercent } from "@/lib/partners/percent"
import { EditPartnerDialog } from "@/components/partners/admin/edit-partner-dialog"
import { AdjustBalanceDialog } from "@/components/partners/admin/adjust-balance-dialog"
import { CreateCodeForPartnerDialog, SetCodeCommissionDialog } from "@/components/partners/admin/admin-code-dialogs"
import type { AdminPartnerCodeView } from "@/server-actions/admin-partners"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





export function AdminPartnerDetail({ partnerId, onBack }: { partnerId: string; onBack: () => void }) {


  const tBonus = useTranslations("PartnerBonus")
  const tUi = useTranslations("Partners.PartnersAdminAdminPartnerDetail")

  const detail = useAdminPartnerDetailQuery(partnerId)
  const [openDialog, setOpenDialog] = useState<"edit" | "adjust" | "new-code" | null>(null)
  const [codeUnderEdit, setCodeUnderEdit] = useState<AdminPartnerCodeView | null>(null)

  if (detail.isError) {
    return (
      <div className="space-y-4">
        <BackButton onBack={onBack} />
        <Alert variant="destructive"><AlertDescription><span key={tUi("unableToLoadThisPartnerPleaseRefreshAnd")}>{tUi("unableToLoadThisPartnerPleaseRefreshAnd")}</span></AlertDescription></Alert>
      </div>
    )
  }

  if (detail.isPending) {
    return (
      <div className="space-y-4">
        <BackButton onBack={onBack} />
        <div className="flex min-h-32 items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> <span key={tUi("loadingPartner")}>{tUi("loadingPartner")}</span></div>
      </div>
    )
  }

  const { partner, codes, referrals } = detail.data

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BackButton onBack={onBack} />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h2 className="text-xl font-semibold">{partner.tenant_name}</h2>
            {partner.status === PARTNER_ACCOUNT_STATUSES.SUSPENDED && <Badge variant="destructive"><span key={tUi("suspended")}>{tUi("suspended")}</span></Badge>}
            {!partner.program_enabled && <Badge variant="secondary"><span key={tUi("programOff")}>{tUi("programOff")}</span></Badge>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpenDialog("edit")}><span key={tUi("editPartner")}>{tUi("editPartner")}</span></Button>
          <Button variant="outline" size="sm" onClick={() => setOpenDialog("adjust")}><TranslationText>{tUi.rich("tag0Tag0AdjustBalance", { tag0: () => (<Scale className="mr-1.5 h-4 w-4" />) })}</TranslationText></Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={tUi("partnerBalance")} value={formatMicroUsd(partner.balance_microusd)} />
        <StatCard label={tUi("earnedInTotal")} value={formatMicroUsd(partner.lifetime_earned_microusd)} />
        <StatCard
          label={tUi("clientBonusesPaid")}
          value={formatMicroUsd(partner.lifetime_client_bonus_microusd)}
          hint={tUi("walletCreditHandedToReferredClientsOutOf")}
        />
        <StatCard
          label={tUi("commission")}
          value={`${formatPercent(partner.commission_percent)}%`}
          hint={partner.commission_percent_override === null ? "Platform default" : "Partner override"}
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="text-base"><span key={tUi("referralLinks")}>{tUi("referralLinks")}</span></CardTitle>
            <CardDescription>
              <span key={tUi("aPerLinkCommissionOverridesThePartnerS")}>{tUi("aPerLinkCommissionOverridesThePartnerS")}</span></CardDescription>
          </div>
          <Button size="sm" onClick={() => setOpenDialog("new-code")}>
            <Plus className="mr-1.5 h-4 w-4" /> <span key={tUi("newLink")}>{tUi("newLink")}</span></Button>
        </CardHeader>
        <CardContent>
          {codes.length ? (
            <Table className="min-w-0">
              <TableHeader>
                <TableRow>
                  <TableHead><span key={tUi("link")}>{tUi("link")}</span></TableHead>
                  <TableHead className="text-right"><span key={tUi("commission")}>{tUi("commission")}</span></TableHead>
                  <TableHead><span key={tUi("clientBonus")}>{tUi("clientBonus")}</span></TableHead>
                  <TableHead className="text-right"><span key={tUi("referred")}>{tUi("referred")}</span></TableHead>
                  <TableHead className="text-right"><span key={tUi("earned")}>{tUi("earned")}</span></TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map(code => (
                  <TableRow key={code.id}>
                    <TableCell className="sm:min-w-48">
                      <NameWithBadges name={code.label ?? "Referral link"}>
                        {!code.is_active && <Badge variant="secondary"><span key={tUi("inactive")}>{tUi("inactive")}</span></Badge>}
                      </NameWithBadges>
                      <p className="mt-0.5 font-mono text-xs text-muted-foreground">{code.code}</p>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <span>{<span key={tUi("value0", { value0: String(formatPercent(code.commission_percent)) })}>{tUi("value0", { value0: String(formatPercent(code.commission_percent)) })}</span>}</span>
                      {code.commission_percent_override === null && (
                        <span className="ml-1 text-xs text-muted-foreground"><span key={tUi("inherited")}>{tUi("inherited")}</span></span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{describeBonus(code, tBonus)}</TableCell>
                    <TableCell className="text-right">{code.referred_count}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{formatMicroUsd(code.earned_microusd)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setCodeUnderEdit(code)}><span key={tUi("setCommission")}>{tUi("setCommission")}</span></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground"><span key={tUi("thisPartnerHasNoReferralLinksYet")}>{tUi("thisPartnerHasNoReferralLinksYet")}</span></p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("referredClients")}>{tUi("referredClients")}</span></CardTitle>
          <CardDescription><span key={tUi("whatEachReferredTenantHasGeneratedForThis")}>{tUi("whatEachReferredTenantHasGeneratedForThis")}</span></CardDescription>
        </CardHeader>
        <CardContent>
          {referrals.length ? (
            <Table className="min-w-0">
              <TableHeader>
                <TableRow>
                  <TableHead><span key={tUi("client")}>{tUi("client")}</span></TableHead>
                  <TableHead><span key={tUi("attributed")}>{tUi("attributed")}</span></TableHead>
                  <TableHead><span key={tUi("link")}>{tUi("link")}</span></TableHead>
                  <TableHead className="text-right"><span key={tUi("commissionGenerated")}>{tUi("commissionGenerated")}</span></TableHead>
                  <TableHead className="text-right"><span key={tUi("bonusReceived")}>{tUi("bonusReceived")}</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map(referral => (
                  <TableRow key={referral.tenant_id}>
                    <TableCell className="font-medium [overflow-wrap:anywhere] sm:min-w-48">{referral.tenant_name}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatPartnerDate(referral.attributed_at)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{referral.code ?? "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{formatMicroUsd(referral.commission_generated_microusd)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{formatMicroUsd(referral.bonus_received_microusd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground"><span key={tUi("noClientsAttributedToThisPartnerYet")}>{tUi("noClientsAttributedToThisPartnerYet")}</span></p>
          )}
        </CardContent>
      </Card>

      <EditPartnerDialog
        open={openDialog === "edit"}
        partner={partner}
        codes={codes}
        onOpenChange={open => setOpenDialog(open ? "edit" : null)}
      />
      <AdjustBalanceDialog
        open={openDialog === "adjust"}
        partner={partner}
        onOpenChange={open => setOpenDialog(open ? "adjust" : null)}
      />
      <CreateCodeForPartnerDialog
        open={openDialog === "new-code"}
        partner={partner}
        onOpenChange={open => setOpenDialog(open ? "new-code" : null)}
      />
      <SetCodeCommissionDialog
        code={codeUnderEdit}
        partnerPercent={partner.commission_percent}
        onOpenChange={open => !open && setCodeUnderEdit(null)}
      />
    </div>
  )
}

function BackButton({ onBack }: { onBack: () => void }) {
  const tUi = useTranslations("Partners.PartnersAdminAdminPartnerDetail")

  return (
    <Button variant="ghost" size="sm" onClick={onBack}>
      <ArrowLeft className="mr-1.5 h-4 w-4" /> <span key={tUi("allPartners")}>{tUi("allPartners")}</span></Button>
  )
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardDescription>{label}</CardDescription></CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold">{value}</p>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  )
}

function describeBonus(code: AdminPartnerCodeView, tBonus: PartnerBonusTranslator): string {
  if (code.client_bonus_percent <= 0) return "—"
  return `${formatPercent(code.client_bonus_percent)}%, ${bonusScheduleLabel(code.client_bonus_payment_limit, tBonus)}`
}
